import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";

export const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "Education",
  "Entertainment",
  "Retail",
  "Manufacturing",
  "Real Estate"
];

export const RECOMMENDED_TAGS = [
  "Tutorial",
  "News",
  "Opinion",
  "Review",
  "Guide",
  "Tips",
  "Case Study",
  "Research",
  "How-to",
  "Analysis",
  "Interview",
  "Trends"
];

interface ProjectTypeSelectorProps {
  selectedTypes: string[];
  onTypeToggle: (type: string) => void;
}

export const ProjectTypeSelector = ({ selectedTypes, onTypeToggle }: ProjectTypeSelectorProps) => {
  const [customTag, setCustomTag] = useState("");

  const handleIndustrySelect = (value: string) => {
    if (!selectedTypes.includes(value.toLowerCase())) {
      onTypeToggle(value);
    }
  };

  const handleCustomTagAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && customTag.trim()) {
      onTypeToggle(customTag.trim());
      setCustomTag("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-gray-200">Industry</Label>
        <Select onValueChange={handleIndustrySelect}>
          <SelectTrigger className="bg-black/30 border-gray-800 text-white">
            <SelectValue placeholder="Select an industry" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-800">
            {INDUSTRIES.map((industry) => (
              <SelectItem key={industry} value={industry} className="text-white hover:bg-gray-800">
                {industry}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-200">Selected Tags</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTypes.map((type) => (
            <Badge 
              key={type} 
              variant="secondary" 
              className="bg-gray-700 text-white border-0 cursor-pointer hover:bg-red-900"
              onClick={() => onTypeToggle(type)}
            >
              #{type.toLowerCase()}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-200">Recommended Tags</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {RECOMMENDED_TAGS.map((type) => (
            <div key={type} className="flex items-center space-x-2">
              <Checkbox
                id={`type-${type}`}
                checked={selectedTypes.includes(type.toLowerCase())}
                onCheckedChange={() => onTypeToggle(type)}
                className="border-gray-600 data-[state=checked]:bg-white data-[state=checked]:text-gray-900"
              />
              <label
                htmlFor={`type-${type}`}
                className="text-sm font-medium text-gray-200 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {type}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-200">Custom Tags</Label>
        <Input
          value={customTag}
          onChange={(e) => setCustomTag(e.target.value)}
          onKeyDown={handleCustomTagAdd}
          placeholder="Type and press Enter to add custom tag"
          className="bg-black/30 border-gray-800 text-white placeholder-gray-500"
        />
      </div>
    </div>
  );
};